/* eslint-disable max-lines */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Form } from 'react-final-form';
import PropTypes from 'prop-types';
import { LoadingButton } from '@mui/lab';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { transformAndGenerateHash } from 'common/utils';
import PatientBanner from 'common/modules/PatientBanner';
import React, { useEffect, useState } from 'react';
import { differenceBy, isEmpty, map } from 'lodash';
import { listPatientOrders, updateOrderAddress } from 'store/thunks/patientThunks';
import { useDispatch, useSelector } from 'react-redux';
import RadioGroupField from 'common/forms/RadioGroupField';
import CheckboxField from 'common/forms/CheckboxField';
import NewPatientAddressButton from '../ShippingTab/NewPatientAddressButton';

const AddressValidationDialog = ({
  open,
  onClose,
  order,
  patientId,
  requireManualConfirmation = false,
  onEmitData,
  suggestedAddresses = [],
}) => {
  const dispatch = useDispatch();

  const [hasInitializedShippingAddress, setHasInitializedShippingAddress] = useState(false);
  const [getSuggestedAddress, setGetSuggestedAddress] = useState('');
  const [selectedSuggestedAddress, setSelectedSuggestedAddress] = useState(null);

  const [initialPatientAddresses, setInitialPatientAddresses] = useState([]);
  const [currentOrderShippingAddress, setCurrentOrderShippingAddress] = useState(null);

  const [formattedPatientAddresses, setFormattedPatientAddresses] = useState([]);
  const [isManuallyMarkedSelected, setIsManuallyMarkedSelected] = useState(false);
  const [unVerifiedAddress, setUnVerifiedAddress] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [initialAddressData, setInitialAddressData] = useState({});

  const [loading, setLoading] = useState(false);

  const patientAddresses = useSelector(({ patient }) => patient[patientId]?.addresses);

  const initialShippingAddress = order?.shippingAddress;

  useEffect(() => {
    // Skip until the addresses are actually loaded — otherwise formattedPatientAddresses is set
    // to undefined and the .map() in the render below throws. An empty array is a valid loaded
    // state and must still fall through.
    if (!patientAddresses) return;

    const unVerifiedAddressIds = patientAddresses
      ?.filter((item) => !item?.isVerified)
      .map((item) => item?.patientAddressId);

    if (unVerifiedAddressIds) {
      setUnVerifiedAddress(unVerifiedAddressIds);
    }

    const formatted = patientAddresses?.map((item) => {
      const { addressLine1, addressLine2, city, state, zip } = item.address;
      const fullAddress = [addressLine1, addressLine2, `${city}, ${state} ${zip}`]
        .filter(Boolean)
        .join(', ');

      return { ...item, fullAddress };
    });

    setFormattedPatientAddresses(formatted);

    if (!hasInitializedShippingAddress) {
      setHasInitializedShippingAddress(true);

      setInitialPatientAddresses(formatted);
      setCurrentOrderShippingAddress(initialShippingAddress);

      const matchedAddress = patientAddresses?.find((add) => {
        const { address } = add;

        const patientAddressString = `${address.addressLine1}${address.addressLine2 || ''}${
          address.city
        }${address.state}${address.zip}`;

        const shippingAddressString = `${initialShippingAddress.line1}${
          initialShippingAddress.line2 || ''
        }${initialShippingAddress.city}${initialShippingAddress.state}${
          initialShippingAddress.zip
        }`;

        const patientAddressHash = transformAndGenerateHash(patientAddressString);

        const orderShippingAddressHash = transformAndGenerateHash(shippingAddressString);

        return orderShippingAddressHash === patientAddressHash;
      });

      setInitialAddressData(matchedAddress);

      if (matchedAddress?.isVerified) {
        setSelectedAddressId(matchedAddress.patientAddressId);
      }

      return;
    }

    const newAddedAddress = differenceBy(formatted, initialPatientAddresses, 'patientAddressId');

    if (!isEmpty(newAddedAddress)) {
      const newAddress = newAddedAddress[0];

      if (newAddress.isVerified) {
        setSelectedAddressId(newAddress.patientAddressId);
      }

      const { addressLine1, addressLine2, city, state, zip } = newAddress.address;

      setCurrentOrderShippingAddress({
        line1: addressLine1,
        line2: addressLine2,
        city,
        state,
        zip,
      });

      setGetSuggestedAddress('');
      setSelectedSuggestedAddress(null);
      setIsManuallyMarkedSelected(false);
    }
  }, [
    hasInitializedShippingAddress,
    initialPatientAddresses,
    initialShippingAddress,
    patientAddresses,
    setInitialAddressData,
  ]);

  const handleAddressSelectChange = (event, form) => {
    const selectedPatientAddressId = event.target.value;
    setSelectedAddressId(selectedPatientAddressId);

    setIsManuallyMarkedSelected(false);
    setGetSuggestedAddress('');
    setSelectedSuggestedAddress(null);

    form.change('addressValidation', false);
    form.change('addressChoice', '');

    const selectedOrdersAddress = patientAddresses.find(
      (address) => address.patientAddressId === selectedPatientAddressId
    );

    if (selectedOrdersAddress) {
      const { addressLine1, addressLine2, city, state, zip } = selectedOrdersAddress.address;

      const formattedShippingAddress = {
        line1: addressLine1,
        line2: addressLine2,
        city,
        state,
        zip,
      };

      setCurrentOrderShippingAddress(formattedShippingAddress);
    }
  };

  const handleExistingAddressCheckboxChange = (value, form) => {
    const isChecked = value.target.checked;
    setIsManuallyMarkedSelected(isChecked);

    if (isChecked) {
      setGetSuggestedAddress('');
      form.change('addressChoice', '');

      setSelectedSuggestedAddress(null);
    }
  };

  const handleSuggestedSelection = (index, form) => {
    setGetSuggestedAddress(String(index));
    setSelectedSuggestedAddress(suggestedAddresses[index]);
    setIsManuallyMarkedSelected(false);
    form.change('addressValidation', false);

    const { line1, line2, city, state, zip } = suggestedAddresses[index];
    setCurrentOrderShippingAddress({ line1, line2, city, state, zip });
  };

  const isSelectedAddressSameAsInitialAddress =
    selectedAddressId === initialAddressData?.patientAddressId;

  const handleAddressVerification = (values) => {
    setLoading(true);
    if (!isEmpty(selectedSuggestedAddress)) {
      const { line1, line2, city, state, zip } = selectedSuggestedAddress;

      const addressInput = {
        nameOnAddress: initialAddressData.nameOnAddress,
        addressLine1: line1,
        addressLine2: line2,
        city,
        state,
        zip,
      };

      setLoading(true);
      dispatch(
        updateOrderAddress({
          mpi: patientId,
          orderId: order.orderId,
          addressInput,
          isAutoShipNeeded: values.AutoBatching,
        })
      )
        .then(() => {
          dispatch(listPatientOrders({ mpi: patientId }));
        })
        .then(() => {
          onEmitData(true);
          setLoading(false);
        });
    }

    if (isManuallyMarkedSelected || !isEmpty(selectedAddressId)) {
      const patientAddressString = `${currentOrderShippingAddress.line1}${
        currentOrderShippingAddress.line2 || ''
      }${currentOrderShippingAddress.city}${currentOrderShippingAddress.state}${
        currentOrderShippingAddress.zip
      }`;

      const shippingAddressString = `${initialShippingAddress.line1}${
        initialShippingAddress.line2 || ''
      }${initialShippingAddress.city}${initialShippingAddress.state}${initialShippingAddress.zip}`;

      const patientAddressHash = transformAndGenerateHash(patientAddressString);
      const orderShippingAddressHash = transformAndGenerateHash(shippingAddressString);

      if (patientAddressHash === orderShippingAddressHash) {
        onEmitData(true);
        return;
      }

      const selectedAddress = patientAddresses.find(
        (address) => address.patientAddressId === selectedAddressId
      );

      const { address, nameOnAddress } = selectedAddress;

      const { addressLine1, addressLine2, city, state, zip } = address;

      const addressInput = {
        nameOnAddress,
        addressLine1,
        addressLine2,
        city,
        state,
        zip,
        ...(isManuallyMarkedSelected &&
          selectedAddress?.isVerified === false && {
            isManuallyVerified: true,
            isValidationRequired: false,
          }),
      };

      setLoading(true);
      dispatch(
        updateOrderAddress({
          mpi: patientId,
          orderId: order.orderId,
          addressInput,
          isAutoShipNeeded: values.AutoBatching,
        })
      )
        .then(() => {
          return dispatch(listPatientOrders({ mpi: patientId }));
        })
        .then(() => {
          onEmitData(true);
          setLoading(false);
        });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Shipping Address Validation </DialogTitle>
      <Form
        onSubmit={handleAddressVerification}
        initialValues={{
          AutoBatching: true,
        }}
        render={({ handleSubmit, form }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Typography variant='subtitle1' gutterBottom>
                Order ID: <strong>{order.orderId}</strong>
              </Typography>

              <Grid container sx={{ mb: 2 }}>
                <PatientBanner mpi={patientId} />
              </Grid>

              <Grid container justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
                <Grid item>
                  <Typography variant='h7' mb={1} ml={0.5}>
                    <strong>Shipping Address</strong>
                  </Typography>
                </Grid>
                <Grid item>
                  <NewPatientAddressButton mpi={patientId}>Add New Address</NewPatientAddressButton>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {currentOrderShippingAddress && (
                    <Box mt={-2} ml={1}>
                      <Typography>{currentOrderShippingAddress.line1}</Typography>
                      {currentOrderShippingAddress.line2 && (
                        <Typography>{currentOrderShippingAddress.line2}</Typography>
                      )}
                      <Typography>{currentOrderShippingAddress.city}</Typography>
                      <Typography>{currentOrderShippingAddress.state}</Typography>

                      <Typography>{currentOrderShippingAddress.zip}</Typography>

                      {selectedAddressId && unVerifiedAddress.includes(selectedAddressId) && (
                        <Box display='flex' alignItems='center' mt={1}>
                          <WarningAmberIcon sx={{ color: 'red', mr: 0.5, fontSize: 14 }} />
                          <Typography color='red' sx={{ fontSize: 12 }}>
                            Could not verify address
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id='select-existing-address-label'>
                      Select existing address
                    </InputLabel>
                    <Select
                      labelId='select-existing-address-label'
                      name='select-existing-address'
                      id='select-existing-address'
                      value={selectedAddressId}
                      label='Select another address'
                      onChange={(event) => handleAddressSelectChange(event, form)}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 150,
                            overflowY: 'auto',
                          },
                        },
                      }}
                    >
                      {formattedPatientAddresses.map((address) => (
                        <MenuItem key={address.patientAddressId} value={address.patientAddressId}>
                          <Typography sx={{ whiteSpace: 'pre-line' }}>
                            {address.fullAddress}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {selectedAddressId && unVerifiedAddress.includes(selectedAddressId) && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
              )}

              {requireManualConfirmation &&
                selectedAddressId &&
                unVerifiedAddress.includes(selectedAddressId) && (
                  <>
                    <Typography sx={{ mt: 2, ml: 1 }}>
                      We were unable to verify the entered address. Please review the options
                      carefully and confirm its accuracy before proceeding.
                    </Typography>

                    {!isEmpty(suggestedAddresses) && (
                      <>
                        <Typography sx={{ mt: 2, mb: 1 }}>Similar Address found:</Typography>
                        <Grid container direction='column'>
                          <RadioGroupField
                            id='AddressChoice-radioField'
                            name='addressChoice'
                            value={getSuggestedAddress}
                          >
                            <Stack direction='column' alignItems='flex-start' gap={2}>
                              {map(suggestedAddresses, (address, index) => (
                                <FormControlLabel
                                  key={`validateAddress-${index}`}
                                  value={String(index)}
                                  control={
                                    <Radio
                                      sx={{ mr: 1 }}
                                      onChange={() => handleSuggestedSelection(index, form)}
                                    />
                                  }
                                  sx={{ px: 1 }}
                                  label={
                                    <ListItemText
                                      secondary={
                                        <>
                                          <Typography>{address.line1}</Typography>
                                          {address?.line2 && (
                                            <Typography>{address.line2}</Typography>
                                          )}
                                          <Typography>
                                            {address.city}, {address.state}, {address.zip}
                                          </Typography>
                                        </>
                                      }
                                    />
                                  }
                                />
                              ))}
                            </Stack>
                          </RadioGroupField>
                        </Grid>
                      </>
                    )}

                    <Typography sx={{ mt: 2, fontWeight: 500, ml: 1 }}>
                      Mark address as verified and continue
                    </Typography>

                    <FormControlLabel
                      sx={{ ml: 1, mb: 0 }}
                      control={
                        <CheckboxField
                          id='AddressValidation-checkbox'
                          name='addressValidation'
                          checked={isManuallyMarkedSelected}
                          onChange={(value) => handleExistingAddressCheckboxChange(value, form)}
                          label={
                            currentOrderShippingAddress
                              ? `${currentOrderShippingAddress.line1}, ${
                                  currentOrderShippingAddress.line2 || ''
                                } ${currentOrderShippingAddress.city}, ${
                                  currentOrderShippingAddress.state
                                }, ${currentOrderShippingAddress.zip}`
                              : 'Loading address...'
                          }
                        />
                      }
                    />

                    {!isSelectedAddressSameAsInitialAddress && (
                      <Alert
                        severity='info'
                        icon={false}
                        sx={{
                          '& .MuiAlert-message': {
                            padding: 0,
                            margin: 0,
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                      >
                        <CheckboxField
                          id='AutoBatching-checkbox'
                          name='AutoBatching'
                          size='small'
                          disableHelperText
                          label={
                            <Box display='flex' alignItems='center' gap={1}>
                              <span>Batch the order with the updated shipping address.</span>
                              <Tooltip
                                title={
                                  form?.getState()?.values?.AutoBatching
                                    ? 'Unclick to disable batching with the updated shipping address'
                                    : 'Click to batch the order with the updated shipping address'
                                }
                              >
                                <InfoOutlinedIcon fontSize='medium' />
                              </Tooltip>
                            </Box>
                          }
                        />
                      </Alert>
                    )}
                  </>
                )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  onClose();
                }}
                variant='outlined'
              >
                Close
              </Button>
              <LoadingButton
                type='submit'
                variant='contained'
                color='primary'
                disabled={
                  !isManuallyMarkedSelected &&
                  !selectedSuggestedAddress &&
                  (isEmpty(selectedAddressId) || unVerifiedAddress.includes(selectedAddressId))
                }
                loading={loading}
              >
                Confirm
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

AddressValidationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  order: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    shippingAddress: PropTypes.shape({
      line1: PropTypes.string.isRequired,
      line2: PropTypes.string,
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      zip: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  patientId: PropTypes.string.isRequired,
  requireManualConfirmation: PropTypes.bool.isRequired,
  suggestedAddresses: PropTypes.arrayOf(
    PropTypes.shape({
      line1: PropTypes.string.isRequired,
      line2: PropTypes.string,
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      zip: PropTypes.string.isRequired,
    })
  ),
  onEmitData: PropTypes.func,
};

AddressValidationDialog.defaultProps = {
  suggestedAddresses: [],
  onEmitData: () => {},
};

export default AddressValidationDialog;
